// 取込実行（中身/TMS・Supabase結線）。純粋な plan.js を collections.js のCRUDで永続化する。
// 決定的docIdでUPSERT＝①再取込しても重複行を作らない。②内容変更は本体更新＋tripChangesに差分。
// 書込先は vendor.target（trips / tachographs）。trips は確定時に相手先名寄せ（正式名称・コード＋枝番）を付与。
import { COLLECTIONS, listDocs, upsert } from '../collections.js'
import { VENDORS } from './vendors.js'
import { planImport, existingMapFromDocs } from './plan.js'
import { enrichTrip, ensureMasters } from '../masters/resolve.js'

const nowIso = () => new Date().toISOString()
const targetOf = (vendor) => COLLECTIONS[vendor.target || 'trips']

// 既存（同一source・同一コレクション）を読み、キー→{signature,values,docId} に畳む。①②の判定材料。
export async function loadExisting(vendorId) {
  const vendor = VENDORS[vendorId]
  const docs = await listDocs(targetOf(vendor))
  return existingMapFromDocs(docs, vendor)
}

// 既存を読み込んでプランを作る（プレビュー用）。
export async function buildPlan({ vendorId, text, ctx = {} }) {
  const existing = await loadExisting(vendorId)
  return planImport({ vendorId, text, existing, ctx })
}

// プランを確定（Supabaseへ書き込み）。unchanged/error はスキップ。new/changed のみ UPSERT。
export async function commitPlan(plan, { fileName = '', user = '' } = {}) {
  const vendor = VENDORS[plan.vendorId]
  const coll = targetOf(vendor)
  const isTrips = (vendor.target || 'trips') === 'trips'
  // 名寄せ表は認証つきで取りに行く（バンドル同梱をやめたため）。
  // ループに入る前に確保する。未読み込みのまま書くと正式名称なしのデータが残る
  if (isTrips) await ensureMasters()
  const runId = `imp_${Date.now()}_${plan.vendorId}`
  const at = nowIso()
  let written = 0, changed = 0
  for (const row of plan.rows) {
    if (row.status === 'unchanged' || row.status === 'error' || row.status === 'skipped') continue
    const body = isTrips ? enrichTrip(row.trip) : row.trip // trips は名寄せ付与
    const doc = { ...body, sourceKey: row.key, _sig: row.sig, updatedAt: at, lastImportId: runId }
    if (row.status === 'new') doc.importedAt = at
    await upsert(coll, row.docId, doc)
    written++
    if (row.status === 'changed') {
      changed++
      await upsert(COLLECTIONS.tripChanges, `${row.docId}__${runId}`, {
        tripId: row.docId, collection: coll, vendor: plan.vendorId, at, runId, diff: row.diff,
      })
    }
  }
  await upsert(COLLECTIONS.imports, runId, {
    vendor: plan.vendorId, target: coll, fileName, user, at,
    summary: plan.summary, written, changed,
  })
  return { ...plan, committed: true, runId, written, changed }
}

// ワンショット（プレビュー＝commit:false／確定＝commit:true）。
export async function runImport({ vendorId, text, ctx = {}, fileName = '', user = '', commit = false }) {
  const plan = await buildPlan({ vendorId, text, ctx })
  if (!commit) return { ...plan, committed: false }
  return commitPlan(plan, { fileName, user })
}
